import React from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import {Box, Text, FontVariant, Scrollbars} from '@pexip/components';
import {useArrowKeyListNavigation} from '../../hooks';

import styles from './SuggestionList.module.scss';

import type {SuggestionItem} from '../../types';
import {SuggestionItemView} from '../SuggestionItem/SuggestionItem.view';

export const SuggestionListView: React.FC<{
    id?: string;
    recents: SuggestionItem[];
    favorites: SuggestionItem[];
    directories: SuggestionItem[];
    isMobile: boolean;
    onItemClick: (item: SuggestionItem) => void;
    onItemFavoriteToggle: (
        item: SuggestionItem,
        newIsFavorite: boolean,
    ) => void;
    className?: string;
    itemClassName?: string;
    width?: number;
    keepOriginFocusOnNavigate?: boolean;
}> = ({
    id,
    recents,
    favorites,
    directories,
    isMobile,
    onItemClick,
    onItemFavoriteToggle,
    className,
    itemClassName,
    width,
    keepOriginFocusOnNavigate = false,
}) => {
    const {t} = useTranslation();
    const defaultId = React.useId();
    const listContentRef = React.useRef<HTMLDivElement>(null);

    useArrowKeyListNavigation({
        containerRef: listContentRef,
        itemSelector: '[data-keyboard-nav-item]',
        loop: true,
        keepOriginFocusOnNavigate,
        activateActiveItemOnEnter: keepOriginFocusOnNavigate,
    });

    const styleVariables = width
        ? ({
              '--suggestion-list-width': `${width}px`,
          } as React.CSSProperties)
        : undefined;

    return (
        <Box
            padding="none"
            className={cx(styles.list, {[styles.hasWidth]: !!width}, className)}
            style={styleVariables}
        >
            <Scrollbars
                className={styles.scrollbars}
                classNames={{contentEl: styles.content}}
            >
                <div id={id ?? defaultId} ref={listContentRef} role="listbox">
                    {favorites.length > 0 && (
                        <div>
                            <Text
                                htmlTag="p"
                                className={styles.sectionTitle}
                                variant="subtle"
                                fontVariant={FontVariant.Body}
                            >
                                {t('media.call-history.favorites', 'Favorites')}
                            </Text>
                            {favorites.map((item, i) => (
                                <SuggestionItemView
                                    className={cx(itemClassName, {
                                        [styles.itemDivider]:
                                            i < favorites.length - 1,
                                    })}
                                    key={`favorite-${item.meetingId}`}
                                    title={item.title}
                                    meetingId={item.meetingId}
                                    imageUrl={item.avatarImageUrl}
                                    isMobile={isMobile}
                                    isFavorite={true}
                                    isBrandingFavorite={
                                        !!item.isBrandingFavorite
                                    }
                                    onClick={() => onItemClick(item)}
                                    onFavoriteToggle={() =>
                                        onItemFavoriteToggle(
                                            item,
                                            !item.isFavorite,
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {recents.length > 0 && (
                        <div>
                            <Text
                                htmlTag="p"
                                className={styles.sectionTitle}
                                variant="subtle"
                                fontVariant={FontVariant.Body}
                            >
                                {t('media.suggestions.recent', 'Recent')}
                            </Text>
                            {recents.map((item, i) => (
                                <SuggestionItemView
                                    className={cx(itemClassName, {
                                        [styles.itemDivider]:
                                            i < recents.length - 1,
                                    })}
                                    key={`recent-${item.meetingId}`}
                                    title={item.title}
                                    meetingId={item.meetingId}
                                    imageUrl={item.avatarImageUrl}
                                    isMobile={isMobile}
                                    isFavorite={item.isFavorite}
                                    isBrandingFavorite={
                                        !!item.isBrandingFavorite
                                    }
                                    onClick={() => onItemClick(item)}
                                    onFavoriteToggle={() =>
                                        onItemFavoriteToggle(
                                            item,
                                            !item.isFavorite,
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {directories.length > 0 && (
                        <div>
                            <Text
                                htmlTag="p"
                                className={styles.sectionTitle}
                                variant="subtle"
                                fontVariant={FontVariant.Body}
                            >
                                {t('media.suggestions.directory', 'Directory')}
                            </Text>
                            {directories.map((item, i) => (
                                <SuggestionItemView
                                    className={cx(itemClassName, {
                                        [styles.itemDivider]:
                                            i < directories.length - 1,
                                    })}
                                    key={`directory-${item.meetingId}`}
                                    title={item.title}
                                    meetingId={item.meetingId}
                                    imageUrl={item.avatarImageUrl}
                                    isMobile={isMobile}
                                    isFavorite={item.isFavorite}
                                    isBrandingFavorite={
                                        !!item.isBrandingFavorite
                                    }
                                    onClick={() => onItemClick(item)}
                                    onFavoriteToggle={() =>
                                        onItemFavoriteToggle(
                                            item,
                                            !item.isFavorite,
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Scrollbars>
        </Box>
    );
};

export type SuggestionListProps = React.ComponentProps<
    typeof SuggestionListView
>;
